import { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen } from '@fortawesome/free-solid-svg-icons';
import { Card, CardHead, GhostBtn, StatusPill } from '../../components';
import { P } from '../../theme/tokens';
import RulesFormModal from './RulesFormModal';
import { WEEKDAYS, daysLabel, hoursLabel, minutesLabel, withDefaults } from './constants';
import { clockLabel } from './time';
import { CardNote, HeadTools, RulesGrid } from './styled';

const toMinutes = (hhmm) => {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
};

const toClock = (mins) => `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;

const short = (d) => d.label.slice(0, 3);

const daysText = (days) => {
  const contiguous = days.every((d, i) => i === 0 || WEEKDAYS.indexOf(d) === WEEKDAYS.indexOf(days[i - 1]) + 1);
  return contiguous && days.length > 2 ? `${short(days[0])} to ${short(days[days.length - 1])}` : days.map(short).join(', ');
};

const byDay = (openDays, valueOf) => {
  const groups = new Map();
  openDays.forEach((d) => {
    const label = clockLabel(toClock(valueOf(d)));
    groups.set(label, [...(groups.get(label) || []), d]);
  });
  if (groups.size === 1) return [...groups.keys()][0];
  return [...groups].map(([label, days]) => `${daysText(days)} ${label}`).join(', ');
};

function ruleItems(s) {
  const openDays = WEEKDAYS.filter((d) => s.weeklyHours[d.day]?.length);
  const ranges = (d) => s.weeklyHours[d.day];
  const notSet = !openDays.length;
  return [
    {
      label: 'Earliest start time',
      value: notSet ? 'Not set' : byDay(openDays, (d) => Math.min(...ranges(d).map((r) => toMinutes(r.start))))
    },
    {
      label: 'Latest start time',
      value: notSet
        ? 'Not set'
        : byDay(openDays, (d) => Math.max(...ranges(d).map((r) => toMinutes(r.end))) - s.slotDurationMin)
    },
    { label: 'Tour / appointment duration', value: minutesLabel(s.slotDurationMin) },
    { label: 'Buffer between appointments', value: s.bufferMin ? minutesLabel(s.bufferMin) : 'None' },
    { label: 'Minimum lead time', value: hoursLabel(s.leadTimeHours) },
    { label: 'Booking window', value: `Up to ${daysLabel(s.maxAdvanceDays)} ahead` },
    { label: 'Bookable days', value: notSet ? 'None' : daysText(openDays) },
    { label: 'Time zone', value: s.timezone.replace(/_/g, ' ') }
  ];
}

export default function SchedulingRules({ scheduling, canEdit }) {
  const [editing, setEditing] = useState(false);
  const rules = useMemo(() => withDefaults(scheduling), [scheduling]);
  const items = useMemo(() => ruleItems(rules), [rules]);

  return (
    <Card aria-labelledby="rules-title">
      <CardHead>
        <h2 id="rules-title">Scheduling Rules</h2>
        <StatusPill color={rules.enabled ? P.success : P.subtle}>
          {rules.enabled ? 'Assistant booking on' : 'Assistant booking off'}
        </StatusPill>
        {!rules.enabled && <CardNote>Callers get a follow-up instead of a time.</CardNote>}
        {canEdit && (
          <HeadTools>
            <GhostBtn type="button" onClick={() => setEditing(true)}>
              <FontAwesomeIcon icon={faPen} />
              Edit rules
            </GhostBtn>
          </HeadTools>
        )}
      </CardHead>
      <RulesGrid>
        {items.map((r) => (
          <div key={r.label}>
            <dt>{r.label}</dt>
            <dd>{r.value}</dd>
          </div>
        ))}
      </RulesGrid>
      {editing && <RulesFormModal rules={rules} onClose={() => setEditing(false)} />}
    </Card>
  );
}
